import { useCallback, useState } from "react";
// Relative, not "@/lib/validation", on purpose: this hook is meant to be imported for
// REAL in the tests of the ~70 forms that use it (jest.mock("@/hooks/useFieldErrors",
// () => require(".../hooks/useFieldErrors"))), and there is no "@" alias on the Jest side.
// A "@/..." import here would force every one of those tests to add a second virtual
// passthrough for a module that is pure zod anyway. Same reason lib/api.js requires
// "./apiErrors" relatively.
import { validateWith } from "../lib/validation";

/**
 * Per-field validation error state for a form.
 *
 * Owns ERROR STATE ONLY — never the form values. The form already owns those, and a hook
 * that took them over would have to be adopted wholesale by every screen (each with its
 * own loading, drafts, nested editors, optimistic rows). Keeping the two separate is what
 * makes this adoptable one form at a time.
 *
 * The stored values are i18n KEYS (see lib/validation.js): the component resolves them
 * through its own `t` when it renders, so the same error map is correct in three locales.
 *
 * @returns {{
 *   fieldErrors: Record<string, string>,
 *   validate: (schema: import("zod").ZodType, form: object) => Record<string, string>|null,
 *   clearFieldError: (field: string) => void,
 *   clearAllErrors: () => void,
 *   setFieldErrors: (next: Record<string, string>) => void,
 * }}
 */
export default function useFieldErrors() {
  const [fieldErrors, setFieldErrors] = useState({});

  /**
   * Validates and PUBLISHES the result, then returns it so the caller can branch on the
   * same call: `if (validate(schema, form)) { toast.error(...); return; }`. Returning the
   * map rather than a boolean means a caller that needs the detail (a wizard deciding
   * which step to open) does not have to read state it just set — that state is not
   * visible until the next render.
   */
  const validate = useCallback((schema, form) => {
    const errs = validateWith(schema, form);
    setFieldErrors(errs || {});
    return errs;
  }, []);

  /**
   * Drops one field's error. Called from the field's own onChange: an error is a statement
   * about the value AT SUBMIT TIME and must not outlive the next keystroke in that field,
   * or a user who has already fixed the typo keeps reading a stale complaint.
   */
  const clearFieldError = useCallback((field) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev; // same object → no re-render
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const clearAllErrors = useCallback(() => { setFieldErrors({}); }, []);

  return { fieldErrors, validate, clearFieldError, clearAllErrors, setFieldErrors };
}
